// Wire schemas for agent-ui mentionables: each rich input emits one
// model-visible part that also carries chip metadata. The discriminated
// `mentionableSchema` union (kind: "mention" | "file" | "skill" |
// "filesystem-file" | "source-element" | "prompt-source") is the umbrella;
// the `*UIMessagePart*` schemas mirror the opencode/AI-SDK wire shape and
// carry a mentionable under `metadata.agent`.
import * as z from "zod";

import { integrationScopeKeySchema } from "./agentIntegration";
import { canvasSelectionMentionSchema } from "./canvas/selection";
import { chatPromptSourceSchema } from "./chatMessage";

export const mentionPreviewSchema = z.object({
  url: z.string(),
  mime: z.string(),
  name: z.string(),
});

export const integrationMentionSchema = z
  .object({
    kind: z.literal("mention"),
    integrationKey: z.string(),
    objectType: z.string(),
    object: z
      .object({
        id: z.string(),
        title: z.string().optional(),
        handle: z.string().optional(),
        imageUrl: z.string().optional(),
        preview: mentionPreviewSchema.optional(),
        description: z.string().optional(),
        filepath: z.string().optional(),
        // NOTE (Kevin, 2026-07-29): Opaque id, not always a UUID (e.g. auto-connected `replo-products-auto-<projectId>`).
        connectionId: z.string().optional(),
        connectionScope: integrationScopeKeySchema.optional(),
      })
      .passthrough(),
  })
  .describe("IntegrationMention");

export const fileMentionSchema = z
  .object({
    kind: z.literal("file"),
    attachmentId: z.string().optional(),
    // Durable Agent File id; since `url` is a short-lived signed URL, consumers
    // re-mint a fresh one via `files.getContent` (which re-checks the grant).
    fileId: z.string().optional(),
    sizeBytes: z.number().optional(),
    // Set when copying a sent message's file chip to clipboard so the
    // dehydrated chip can re-emit the same wire shape on paste+submit: if
    // present, the walker routes through `fileResolver`'s text-part branch
    // (font reference description, inline body); if absent, the file-part
    // branch (image/PDF). In-flight composer attachments leave this empty —
    // the upload-store record carries the body directly.
    contentText: z.string().optional(),
    // Set when the file rides on a `text`-typed wire part (inline-presentation
    // bodies, reference-presentation descriptions). The bubble uses these to
    // render the same file chip the user composed, even though the wire part
    // is text rather than file. Image / PDF file parts duplicate the same data
    // on `partData.url / .mediaType / .filename` and don't need this metadata.
    url: z.string().optional(),
    mime: z.string().optional(),
    filename: z.string().optional(),
  })
  .describe("FileMention");

export const skillMentionSchema = z
  .object({
    kind: z.literal("skill"),
    id: z.string(),
    name: z.string(),
    skillShape: z.string().optional(),
    skillGradient: z.string().optional(),
  })
  .describe("SkillMention");

export const filesystemFileMentionSchema = z
  .object({
    kind: z.literal("filesystem-file"),
    path: z.string(),
    name: z.string(),
    range: z
      .object({
        startLine: z.number(),
        endLine: z.number(),
      })
      .optional(),
  })
  .describe("FilesystemFileMention");

// Carries enough about a user-targeted DOM element for the model to find and edit it.
// Used by select-mode (one chip per element) and by mention-style element references.
export const sourceElementMentionSchema = z
  .object({
    kind: z.literal("source-element"),
    label: z.string(),
    tagName: z.string().optional(),
    // Tag as authored in JSX source, when it differs from the rendered DOM
    // `tagName`. Next.js `<Image>` renders a raw `<img>`, so reporting the DOM
    // tag alone would always say `<img>`; this carries `Image` so the agent
    // sees the component the user actually wrote.
    sourceTagName: z.string().optional(),
    elementType: z.string().optional(),
    sourceFilePath: z.string().optional(),
    sourceLineNumber: z.number().optional(),
    sourceColumnNumber: z.number().optional(),
    componentName: z.string().optional(),
    componentId: z.string().optional(),
    currentVisibleText: z.string().optional(),
    sectionLabel: z.string().optional(),
    selectionMode: z.enum(["element", "area"]).optional(),
    // Dictionary dot-path (`hero.title`) when the selected text renders from
    // the site-i18n `dict` convention — tells the model to edit the
    // dictionaries, not the JSX.
    translationKey: z.string().optional(),
  })
  .describe("SourceElementMention");

// Label-only provenance chip — "this message originated from X". Carries no
// model-visible text; the user's actual words ride as a sibling plain-text part.
// UI consumers (e.g. `From <Miniapp>` header) read `source`.
export const promptSourceMentionSchema = z
  .object({
    kind: z.literal("prompt-source"),
    source: chatPromptSourceSchema,
  })
  .describe("PromptSourceMention");

// References a brand's materialized design system on disk. The resolver emits
// model-visible text pointing the agent at `path`; the chip renders `name`.
export const brandMentionSchema = z
  .object({
    kind: z.literal("brand"),
    name: z.string(),
    path: z.string(),
    // Present once the project has project-context rows: the published
    // version the chip was built from and the files tagged as context, so the
    // agent's get_branding call can tell a stale chip from a fresh one.
    version: z.number().int().optional(),
    contextFiles: z
      .array(
        z.object({
          path: z.string(),
          tags: z.array(z.string()),
        }),
      )
      .optional(),
  })
  .describe("BrandMention");

// A highlighted region of a file. Unlike `filesystem-file` (a path reference),
// this carries the selected `snippet` text inline so the resolver can embed the
// exact lines as a fenced code block — the model never re-reads the file. The
// chip renders `filename (L{startLine}-{endLine})`.
export const fileSnippetMentionSchema = z
  .object({
    kind: z.literal("file-snippet"),
    filename: z.string(),
    language: z.string().optional(),
    startLine: z.number(),
    endLine: z.number(),
    snippet: z.string(),
    fileId: z.string().optional(),
  })
  .describe("FileSnippetMention");

// A workspace member mentioned in a comment. Only the id is stored; name and
// avatar resolve at render time so renames and deletions show correctly.
export const userMentionSchema = z
  .object({
    kind: z.literal("user"),
    userId: z.string(),
  })
  .describe("UserMention");

export const mentionableSchema = z
  .discriminatedUnion("kind", [
    integrationMentionSchema,
    fileMentionSchema,
    skillMentionSchema,
    filesystemFileMentionSchema,
    sourceElementMentionSchema,
    promptSourceMentionSchema,
    brandMentionSchema,
    fileSnippetMentionSchema,
    canvasSelectionMentionSchema,
    userMentionSchema,
  ])
  .describe("Mentionable");

export const uiMessagePartMetadataSchema = z
  .object({
    agent: mentionableSchema.optional(),
    supportingFiles: z
      .array(z.string())
      .optional()
      .describe(
        "Files (Agent File id, or url when there is none) the model reads alongside this chip's part. Stored file parts lose their metadata, so the chip's own part names them; the bubble hides them.",
      ),
  })
  .passthrough()
  .describe("UIMessagePartMetadata");

export const textUIMessagePartSchema = z
  .object({
    type: z.literal("text"),
    id: z.string().optional(),
    text: z.string(),
    synthetic: z.boolean().optional(),
    ignored: z.boolean().optional(),
    metadata: uiMessagePartMetadataSchema.optional(),
  })
  .describe("TextUIMessagePart");

// Mirrors opencode's `FileSource` (the `type: "file"` arm of `FilePartSource`).
// Unlike `metadata`, opencode preserves `source` across its file-part round-trip,
// so it's the only durable carrier for a file part's identity. We ride the
// durable Agent File id inside `source.path` as `fileid://{fileId}` (see
// `encodeFileSource`); `text` is required by the union but unused, so it's empty.
export const filePartSourceSchema = z
  .object({
    type: z.literal("file"),
    path: z.string(),
    text: z.object({
      value: z.string(),
      start: z.number(),
      end: z.number(),
    }),
  })
  .describe("FilePartSource");

export const fileUIMessagePartSchema = z
  .object({
    type: z.literal("file"),
    id: z.string().optional(),
    url: z.string(),
    mediaType: z.string(),
    filename: z.string().optional(),
    synthetic: z.boolean().optional(),
    // Durable identity carrier; opencode preserves `source` but drops `metadata`.
    source: filePartSourceSchema.optional(),
    metadata: uiMessagePartMetadataSchema.optional(),
  })
  .describe("FileUIMessagePart");

// The only media types Anthropic ingests as file blocks — anything else on a
// `type:"file"` part draws a non-retryable 400 that kills the turn.
const MODEL_INGESTIBLE_FILE_MEDIA_TYPE_LIST = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "application/pdf",
] as const;

// Literal union so a producer of media types (the coordinator's byte sniffer)
// cannot return a type outside this set.
export type ModelIngestibleFileMediaType =
  (typeof MODEL_INGESTIBLE_FILE_MEDIA_TYPE_LIST)[number];

export type ModelIngestibleImageMediaType = Extract<
  ModelIngestibleFileMediaType,
  `image/${string}`
>;

export const MODEL_INGESTIBLE_FILE_MEDIA_TYPES: ReadonlySet<string> = new Set(
  MODEL_INGESTIBLE_FILE_MEDIA_TYPE_LIST,
);

export function isModelIngestibleFileMediaType(
  mediaType: string,
): mediaType is ModelIngestibleFileMediaType {
  return MODEL_INGESTIBLE_FILE_MEDIA_TYPES.has(mediaType);
}

// NOTE (Gabe, 2026-08-31): Anthropic rejects PDFs above 31,457,280 bytes with
// a non-retryable 400 (measured by bisecting live API probes on the URL-fetch
// path; their docs claim 32MB). This guard sits ~1.4MB under that so drift on
// their side can't resurface the 400, which poisons the whole session.
export const MAX_MODEL_INGESTIBLE_PDF_BYTES = 30_000_000;

// Anthropic's ~5MB per-image limit. Over-cap images stay on disk as a file
// reference — inlining their base64 stalls the prompt POST.
export const MAX_MODEL_INGESTIBLE_IMAGE_BYTES = 5 * 1024 * 1024;

export function isOversizedModelImage({
  mediaType,
  sizeBytes,
}: {
  mediaType: string;
  sizeBytes?: number;
}): boolean {
  return (
    mediaType.startsWith("image/") &&
    isModelIngestibleFileMediaType(mediaType) &&
    sizeBytes !== undefined &&
    sizeBytes > MAX_MODEL_INGESTIBLE_IMAGE_BYTES
  );
}

// Text attachments above this ride as an on-disk reference the agent reads with tools; base64-heavy files tokenize near 1 char/token, so bytes are the honest unit (REPL-31502).
export const MAX_INLINE_TEXT_BYTES = 64 * 1024;

// `fileid://` is the single scheme for a durable Agent File reference. It rides
// both `source.path` (carrier through opencode) and the persisted file-part
// `url` (after the coordinator rewrite), so there is one decoder everywhere.
// Parse with a prefix check, NEVER `new URL()` (which lowercases/validates the
// scheme and would reject ids that aren't URL-shaped).
export const FILE_REF_PREFIX = "fileid://";

// The one place a `fileid://` reference string is constructed. Both the file
// part `url` (coordinator rewrite) and the `source.path` carrier go through
// here so the two forms can never drift apart from `decodeFileId`.
export function encodeFileRef(fileId: string): string {
  return `${FILE_REF_PREFIX}${fileId}`;
}

export function encodeFileSource({ fileId }: { fileId: string }): {
  type: "file";
  path: string;
  text: { value: string; start: number; end: number };
} {
  return {
    type: "file",
    path: encodeFileRef(fileId),
    text: { value: "", start: 0, end: 0 },
  };
}

// Returns the fileId encoded in a `fileid://{fileId}` string, or null for any
// non-reference value (legacy urls, data: blobs, signed https, undefined).
export function decodeFileId(value: string | undefined | null): string | null {
  if (!value?.startsWith(FILE_REF_PREFIX)) {
    return null;
  }
  const fileId = value.slice(FILE_REF_PREFIX.length);
  return fileId.length > 0 ? fileId : null;
}

// How `supportingFiles` names a file part, before and after storage: its Agent File id, or its url when it has none.
export function getSupportingFileKey(part: {
  url: string;
  source?: { path: string };
}): string {
  return decodeFileId(part.source?.path) ?? decodeFileId(part.url) ?? part.url;
}

export const uiMessagePartSchema = z
  .discriminatedUnion("type", [
    textUIMessagePartSchema,
    fileUIMessagePartSchema,
  ])
  .describe("UIMessagePart");

export const uiMessagePartsSchema = z
  .array(uiMessagePartSchema)
  .describe("UIMessageParts");
